import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useListPackagingSupplierDocuments } from '@/shared/hooks/api/packagingSuppliers'
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { packagingItemShortDocumentTypeLabelKey } from './packagingItemShortDocumentTypeLabelKey'

type SupplierDocumentLibraryTableProps = {
  packagingSupplierId: string
}

function PackagingItemsCountCell({
  count,
  countAriaLabel,
  linkedItemsLabel,
  packagingSupplierId,
  linkedItems,
}: {
  count: number
  countAriaLabel: string
  linkedItemsLabel: string
  packagingSupplierId: string
  linkedItems: { id: string; name: string }[]
}) {
  if (count === 0) {
    return <span>{count}</span>
  }

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <button
          type='button'
          className='cursor-default underline decoration-dotted underline-offset-2'
          aria-label={countAriaLabel}
        >
          {count}
        </button>
      </HoverCardTrigger>
      <HoverCardContent className='w-80 max-w-sm' align='start' side='top'>
        <p className='mb-2 text-xs font-medium text-muted-foreground'>
          {linkedItemsLabel}
        </p>
        <ul className='space-y-2'>
          {linkedItems.map((item) => (
            <li key={item.id} className='text-sm'>
              <Link
                to={`/packaging/suppliers/${packagingSupplierId}/items/${item.id}`}
                className='font-medium text-foreground underline decoration-dotted underline-offset-2'
              >
                {item.name}
              </Link>
            </li>
          ))}
        </ul>
      </HoverCardContent>
    </HoverCard>
  )
}

export function SupplierDocumentLibraryTable({
  packagingSupplierId,
}: SupplierDocumentLibraryTableProps) {
  const { t } = useTranslation('packagingSuppliers')
  const { data: rows = [] } = useListPackagingSupplierDocuments(
    packagingSupplierId
  )

  const columnCount = 3

  return (
    <div className='overflow-x-auto rounded-md border'>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              {t('details.documentLibrary.columnFileName')}
            </TableHead>
            <TableHead>
              {t('details.documentLibrary.columnDocumentType')}
            </TableHead>
            <TableHead>
              {t('details.documentLibrary.columnPackagingItems')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length > 0 ? (
            rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className='font-medium'>{row.fileName}</TableCell>
                <TableCell>
                  {t(packagingItemShortDocumentTypeLabelKey(row.documentType))}
                </TableCell>
                <TableCell>
                  <PackagingItemsCountCell
                    count={row.packagingItemCount}
                    countAriaLabel={t(
                      'details.documentLibrary.packagingItemsCountAria',
                      { count: row.packagingItemCount }
                    )}
                    linkedItemsLabel={t(
                      'details.documentLibrary.linkedItemsLabel'
                    )}
                    packagingSupplierId={packagingSupplierId}
                    linkedItems={row.linkedItems}
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columnCount} className='p-0'>
                <Empty className='rounded-none border-0 py-10'>
                  <EmptyHeader>
                    <EmptyTitle>
                      {t('details.documentLibrary.empty')}
                    </EmptyTitle>
                  </EmptyHeader>
                </Empty>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
